import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../components/Loader.css';
import { GOLD, GOLD_LT, CREAM, INK } from '../theme';
import { findImageByRole } from '../data/imageUrl';
import { responsiveImage, CARD_WIDTHS } from '../data/responsiveImage';

export default function CollectionsCarousel({ collections, imageUrl, carouselRef, resetRef, loading, error, linkBase = '/collections' }) {
  const [loadedThumbs, setLoadedThumbs] = useState({});
  const items = collections || [];
  const showPlaceholders = loading && items.length === 0;
  const showEmptyNotice = !loading && !error && items.length === 0;

  // With nothing published, this rendered a full-height band whose entire
  // content was one dim sentence — and it is where the primary nav's
  // "Collections" link lands. An anchor that resolves to an empty room is
  // worse than no anchor, so the section keeps its id (so the link still has
  // somewhere to go) but hands the visitor on to the categories instead.
  if (showEmptyNotice) {
    return (
      <section id="collections" style={{ padding: 'clamp(56px,8vh,96px) clamp(20px,4vw,60px)', background: INK, borderTop: '1px solid rgba(201,162,75,.2)' }}>
        <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '.36em', textTransform: 'uppercase', color: GOLD, marginBottom: 14 }}>I — The Collection</div>
        <h2 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontStyle: 'italic', fontSize: 'clamp(32px,4.4vw,56px)', lineHeight: .98, margin: '0 0 18px', color: CREAM }}>Between collections</h2>
        <p style={{ fontSize: 18, lineHeight: 1.7, color: 'rgba(239,228,210,.7)', maxWidth: 520, margin: '0 0 26px' }}>
          Our next line is still on the atelier floor. In the meantime, every piece we make is browsable by category.
        </p>
        <Link to="/categories" style={{ display: 'inline-block', fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', padding: '14px 28px', border: `1px solid ${GOLD}`, color: GOLD_LT, textDecoration: 'none' }}>
          Browse by category →
        </Link>
      </section>
    );
  }

  return (
    <section id="collections" style={{ padding: 'clamp(64px,10vh,130px) 0', background: INK, borderTop: '1px solid rgba(201,162,75,.2)' }}>
      <div style={{ padding: '0 clamp(20px,4vw,60px)', marginBottom: 48 }}>
        <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '.36em', textTransform: 'uppercase', color: GOLD, marginBottom: 14 }}>I — The Collection</div>
        <h2 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontStyle: 'italic', fontSize: 'clamp(38px,5.4vw,74px)', lineHeight: .98, margin: 0, color: CREAM }}>Selected silhouettes</h2>
      </div>
      <div ref={carouselRef} className="ij-car" style={{ display: 'flex', gap: 24, overflowX: 'auto', padding: '0 clamp(20px,4vw,60px)' }}>
        {showPlaceholders && Array.from({ length: 3 }).map((_, i) => (
          <div key={`ph-${i}`} style={{ flex: '0 0 auto', width: 'min(78vw,350px)' }}>
            <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', background: 'repeating-linear-gradient(135deg,#2a2016,#2a2016 14px,#231a11 14px,#231a11 28px)', border: '1px solid rgba(201,162,75,.4)' }}>
              <div className="ij-loader-wrap"><span className="ij-loader" /></div>
            </div>
          </div>
        ))}

        {(error && items.length === 0) && (
          <p style={{ color: 'rgba(239,228,210,.55)', fontSize: 15 }}>Collections are temporarily unavailable.</p>
        )}

        {!showPlaceholders && items.length > 0 && [...items, ...items].map((item, i) => {
          const thumb = findImageByRole(item, 'thumb');
          // Second copy exists only so the marquee can wrap seamlessly; it
          // must not be announced or tabbed into.
          const isClone = i >= items.length;
          return (
            <Link
              key={i}
              ref={i === items.length ? resetRef : null}
              to={`${linkBase}/${item.slug}`}
              aria-hidden={isClone ? 'true' : undefined}
              tabIndex={isClone ? -1 : undefined}
              style={{ flex: '0 0 auto', width: 'min(78vw,350px)', textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', background: 'repeating-linear-gradient(135deg,#2a2016,#2a2016 14px,#231a11 14px,#231a11 28px)', border: '1px solid rgba(201,162,75,.4)', overflow: 'hidden' }}>
                {thumb && !loadedThumbs[item.slug] && <div className="ij-loader-wrap"><span className="ij-loader" /></div>}
                {thumb && (
                  <img
                    {...responsiveImage(imageUrl(thumb), CARD_WIDTHS, '(max-width: 640px) 78vw, 350px')}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onLoad={() => setLoadedThumbs((s) => ({ ...s, [item.slug]: true }))}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', opacity: loadedThumbs[item.slug] ? 1 : 0, transition: 'opacity .5s ease' }}
                  />
                )}
                <div style={{ position: 'absolute', inset: 10, border: '1px solid rgba(201,162,75,.2)', pointerEvents: 'none', zIndex: 2 }} />
              </div>
              {/* Name and tagline were pushed to opposite ends of a 350px
                  card, which read as two unrelated labels. Stacked, they read
                  as one caption — and the tagline moves off the near-black
                  crimson it was set in, which sat around 2.6:1. */}
              <div style={{ marginTop: 18 }}>
                <h3 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontSize: 26, margin: '0 0 6px', color: CREAM }}>{item.name}</h3>
                <span style={{ fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', color: GOLD_LT, fontWeight: 600 }}>{item.tagline}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
